import { useCallback, useMemo, useState } from 'react';
import styles from './AvailabilityPage.module.css';
import { Sidebar } from '@/components/Sidebar';
import { TopBar } from '@/components/TopBar';
import { FilterChips, type Chip } from '@/components/FilterChips';
import { DataTable, type TableRow } from '@/components/DataTable';
import { DataSourceBanner } from './DataSourceBanner';
import { OsaMapSection } from './OsaMapSection';
import { useAvailabilityOverview } from '@/hooks/useAvailabilityOverview';
import { adaptTable } from '@/lib/adapt';
import { productImage } from '@/lib/productImages';
import { DEFAULT_RANGE, type DateRange } from '@/lib/dateRange';
import { StoreDrilldownModal, type StoreDrilldownTarget } from './overlays/StoreDrilldownModal';
import { AnalysisDrawer } from './overlays/AnalysisDrawer';
import { loadActiveProfile, loadSession, saveActiveProfile } from '@/lib/auth';
import { titleCase } from '@/lib/format';
import {
  CATEGORY_COLUMNS,
  CATEGORY_ROWS,
  CITY_COLUMNS,
  CITY_ROWS,
  SKU_COLUMNS,
  SKU_ROWS,
} from '@/data/availability.mock';

/**
 * Brand the page opens on, when the signed-in user actually holds it. It is a
 * PREFERENCE, not a fallback: a session with no grants must not quietly query
 * somebody else's brand and show a 403 as though the dashboard were broken.
 * The ACTIVE brand otherwise comes from the profile picker — this used to be
 * the only source, so one build served exactly one tenant and a user with 32
 * grants could see one of them.
 */
const PREFERRED_CUSTOMER = import.meta.env.VITE_CUSTOMER_NAME ?? 'Glenmark';
/**
 * Fixture mode. `?fixture` in the URL is what the visual-regression suite uses:
 * it pins the page to the comp's data with no network at all, so a design diff
 * measures layout and never catches a retry mid-flight.
 */
const FORCE_FIXTURE =
  import.meta.env.VITE_USE_MOCK === 'true' ||
  (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('fixture'));

/**
 * No selection until the user drills in.
 *
 * These used to be seeded from the Figma comp ("Shampoo", "Mumbai"). Real data
 * calls the category "Shampoos" and returns cities lower-cased, so the seed
 * matched nothing and the City and SKU tables rendered empty on first load —
 * a comp string is a label, never a filter value.
 */
type Selection = { category?: string; city?: string; sku?: string };
const INITIAL: Selection = {};

export function AvailabilityPage({
  onSignOut,
  onNavigate,
}: {
  onSignOut?: () => void;
  onNavigate?: (section: string, page: string) => void;
}) {
  const session = loadSession();
  const profiles = useMemo(() => session?.profiles ?? [], [session?.profiles]);
  const [activeProfile, setActiveProfile] = useState<string | null>(() => loadActiveProfile());
  /** No grants means nothing to show — not "show Glenmark to whoever asks". */
  const hasGrants = profiles.length > 0 || (session?.brands?.length ?? 0) > 0;
  const CUSTOMER = activeProfile ?? (hasGrants ? PREFERRED_CUSTOMER : '');
  const [selection, setSelection] = useState<Selection>(INITIAL);
  const [drilldown, setDrilldown] = useState<StoreDrilldownTarget | null>(null);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [trendDays, setTrendDays] = useState(7);
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);

  /**
   * Switching brand clears the drill-down. "Shampoos" in one tenant's catalogue
   * is not the same row in another's, and carrying the filter across would ask
   * for a category the new brand may not sell — an empty table that reads as a
   * data problem rather than a stale filter.
   */
  const changeProfile = (name: string): void => {
    saveActiveProfile(name);
    setActiveProfile(name);
    setSelection(INITIAL);
    setDrilldown(null);
    setAnalysisOpen(false);
  };

  const query = useAvailabilityOverview({
    customerName: CUSTOMER,
    filters: selection,
    range,
    trendDays,
    limit: 6,
    enabled: !FORCE_FIXTURE && CUSTOMER !== '',
  });

  const live = query.data;
  /**
   * The fixture is ONLY ever shown when it was explicitly asked for.
   *
   * It used to stand in whenever live data was absent — including the first
   * paint of every single load — so the numbers a user saw first were the
   * Figma comp's, dressed identically to real ones and carrying no banner
   * (the banner was suppressed while `isLoading`). Wrong numbers that look
   * right are worse than no numbers, and "Shampoo 96.1%" is not a placeholder
   * a reader can tell apart from the truth.
   */
  const usingFixture = FORCE_FIXTURE;
  /**
   * No data yet and nothing to show for it — draw the frame, not values.
   * A failed request is NOT pending: it has finished, badly, and a skeleton
   * that shimmers forever tells the reader the page is still trying.
   */
  const isPending = !FORCE_FIXTURE && !live && !query.isError && hasGrants;

  /**
   * A brand the user is granted but that has no Blinkit data.
   *
   * 25 of this account's 32 grants are in that state, and the tables render as
   * a lone "All" row of dashes — which reads as a broken dashboard rather than
   * an empty one. Say which brand and which window, so the reader knows it is
   * a fact about the data and not a fault.
   */
  const isEmptyBrand = Boolean(
    live && live.tables.category.rows.filter((r) => r.id !== 'total-all').length === 0,
  );

  const tables = useMemo(() => {
    if (FORCE_FIXTURE) {
      const mark = (rows: TableRow[], selected: string | undefined): TableRow[] =>
        rows.map((r) => ({ ...r, selected: r.label === selected }));
      return {
        category: mark(CATEGORY_ROWS, selection.category ?? 'Shampoo'),
        city: mark(CITY_ROWS, selection.city ?? 'Mumbai'),
        sku: mark(SKU_ROWS, selection.sku ?? 'Scalpe Pro Anti Dandruff Sha..'),
      };
    }
    if (live) {
      return {
        category: adaptTable('category', live.tables.category, selection.category),
        city: adaptTable('city', live.tables.city, selection.city, undefined, titleCase),
        sku: adaptTable('sku', live.tables.sku, selection.sku, productImage),
      };
    }
    return { category: [], city: [], sku: [] };
  }, [live, selection]);

  // Each chip is a picker. An unset level reads "All …" and still opens.
  const chips: Chip[] = useMemo(
    () => [
      { kind: 'category', label: selection.category ?? 'All Categories', value: selection.category },
      { kind: 'city', label: titleCase(selection.city) ?? 'All Cities', value: selection.city },
      { kind: 'sku', label: selection.sku ?? 'All SKUs', value: selection.sku },
    ],
    [selection],
  );

  /** Picking from a chip sets that level and drops the levels it narrows. */
  const selectChip = useCallback((kind: Chip['kind'], value: string) => {
    setSelection((prev) => {
      if (kind === 'category') return { category: value };
      if (kind === 'city') {
        const next: Selection = { city: value };
        if (prev.category !== undefined) next.category = prev.category;
        return next;
      }
      const next: Selection = { sku: value };
      if (prev.category !== undefined) next.category = prev.category;
      if (prev.city !== undefined) next.city = prev.city;
      return next;
    });
  }, []);

  /** Clearing a chip drops that level and everything it was narrowing. */
  const clearChip = useCallback((kind: Chip['kind']) => {
    setSelection((prev) => {
      if (kind === 'category') return {};
      if (kind === 'city') {
        const next: Selection = {};
        if (prev.category !== undefined) next.category = prev.category;
        return next;
      }
      const next: Selection = {};
      if (prev.category !== undefined) next.category = prev.category;
      if (prev.city !== undefined) next.city = prev.city;
      return next;
    });
  }, []);

  // Selecting a row narrows that level; selecting its "All" row clears it.
  const pick = useCallback(
    (key: keyof Selection) => (row: TableRow) => {
      setSelection((prev) => {
        const next: Selection = { ...prev };
        // Filter on the raw warehouse value, never the presented label.
        if (row.label === 'All') delete next[key];
        else next[key] = row.value ?? row.label;
        return next;
      });
    },
    [],
  );


  return (
    <div className={styles.shell}>
      <Sidebar activeId="supply-chain" activeSection="availability" {...(onNavigate ? { onNavigate } : {})} />

      <div className={styles.main}>
        <TopBar
          title="Availability"
          trendDays={trendDays}
          onTrendDaysChange={setTrendDays}
          range={range}
          onRangeChange={setRange}
          {...(live?.window ? { resolved: live.window } : {})}
          {...(live?.window?.end ? { maxDate: live.window.end } : {})}
          {...(session?.user?.fullName || session?.user?.email
            ? { userLabel: String(session.user.fullName ?? session.user.email) }
            : {})}
          profiles={profiles}
          activeProfile={activeProfile}
          onProfileChange={changeProfile}
          onSettingsClick={() => { void query.refetch(); }}
          {...(onSignOut ? { onSignOut } : {})}
        />

        <FilterChips
          chips={chips}
          customerName={CUSTOMER}
          onSelect={selectChip}
          onClear={clearChip}
          format={{ city: (v) => titleCase(v) ?? v }}
        />

        <main
          className={styles.content}
          aria-busy={query.isFetching}
          data-fetching={query.isFetching ? 'true' : undefined}
        >
          {usingFixture && <DataSourceBanner kind="fixture" />}
          {!FORCE_FIXTURE && !hasGrants && (
            <p className={styles.emptyBrand}>
              This sign-in has no Blinkit brands assigned. Ask an administrator to
              align your account, then sign in again.
            </p>
          )}
          {isEmptyBrand && !query.isError && (
            <p className={styles.emptyBrand}>
              No Blinkit availability data for <strong>{CUSTOMER}</strong> in this window.
              {profiles.length > 1 && ' Pick another brand from the profile menu, or widen the date range.'}
            </p>
          )}
          {query.isError && (
            <DataSourceBanner
              kind="error"
              {...(query.error ? { detail: query.error.message } : {})}
            />
          )}

          <DataTable
            caption="Availability by category"
            columns={CATEGORY_COLUMNS}
            rows={tables.category}
            loading={isPending}
            onRowClick={pick('category')}
          />
          <DataTable
            caption="Availability by city"
            columns={CITY_COLUMNS}
            rows={tables.city}
            loading={isPending}
            onRowClick={pick('city')}
            onActionsClick={(row) => {
              if (row.label !== 'All') selectChip('city', row.value ?? row.label);
              setAnalysisOpen(true);
            }}
          />
          <DataTable
            caption="Availability by SKU"
            columns={SKU_COLUMNS}
            rows={tables.sku}
            loading={isPending}
            onRowClick={pick('sku')}
            onActionsClick={(row) => {
              if (!row.entityId) return;
              setDrilldown({
                productId: row.entityId,
                skuLabel: row.label,
                ...(selection.city ? { city: selection.city } : {}),
                ...(selection.city ? { cityLabel: titleCase(selection.city) } : {}),
              });
            }}
          />

          {/* OSA View — Figma 2881:30849. Below the tables, same window and
              filters: the map answers "where", the tables answer "what". */}
          {!FORCE_FIXTURE && hasGrants && (
            <OsaMapSection
              customerName={CUSTOMER}
              scope={selection}
              range={range}
              onCityPick={(city) => { selectChip('city', city); }}
            />
          )}
        </main>
      </div>

      {analysisOpen && (
        <AnalysisDrawer
          customerName={CUSTOMER}
          target={selection}
          labels={[
            ...(selection.category ? [selection.category] : []),
            ...(selection.city ? [titleCase(selection.city) ?? selection.city] : []),
            ...(selection.sku ? [selection.sku] : []),
          ]}
          range={range}
          onClose={() => { setAnalysisOpen(false); }}
        />
      )}

      {drilldown && (
        <StoreDrilldownModal
          customerName={CUSTOMER}
          target={drilldown}
          range={range}
          onClose={() => { setDrilldown(null); }}
        />
      )}
    </div>
  );
}


